import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Clock, Flame, Eye, Map, Zap, Sparkles, Trophy, MessageCircle,
  ChevronDown, CheckCircle2,
} from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageHero from "../components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=80";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

// Color/iconography map per block (semantic emotional levers)
const BLOCK_META = {
  red:      { icon: Flame,         hex: "#EF4444", soft: "rgba(239,68,68,0.12)" },
  yellow:   { icon: Eye,           hex: "#F59E0B", soft: "rgba(245,158,11,0.12)" },
  orange:   { icon: Map,           hex: "#FF914D", soft: "rgba(255,145,77,0.14)" },
  electric: { icon: Zap,           hex: "#00D4FF", soft: "rgba(0,212,255,0.14)" },
  green:    { icon: Sparkles,      hex: "#22C55E", soft: "rgba(34,197,94,0.12)" },
  finish:   { icon: Trophy,        hex: "#E67100", soft: "rgba(230,113,0,0.14)" },
  qa:       { icon: MessageCircle, hex: "#8B5CF6", soft: "rgba(139,92,246,0.12)" },
};

// ============================================================
// Timeline bar — shows minute allocation per block proportionally
// ============================================================
function TimelineBar({ blocks }) {
  return (
    <div className="mt-12">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
        <span>Inicio</span>
        <span className="font-semibold text-foreground">2 horas en total</span>
        <span>Fin</span>
      </div>

      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, ease: "easeOut" }}
        className="origin-left h-12 rounded-2xl overflow-hidden border border-border bg-card flex"
      >
        {blocks.map((b, i) => {
          const meta = BLOCK_META[b.color] || BLOCK_META.orange;
          const sum = blocks.reduce((acc, x) => acc + x.minutes, 0);
          const widthPct = (b.minutes / sum) * 100;
          return (
            <motion.div
              key={b.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
              className="relative group flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-white"
              style={{ width: `${widthPct}%`, backgroundColor: meta.hex }}
              title={`${b.title} — ${b.time}`}
            >
              <span className="hidden sm:inline">{b.id}</span>
              <span
                className="absolute inset-x-0 -bottom-7 text-center text-[10px] uppercase tracking-wider text-muted-foreground font-medium opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
              >
                {b.time}
              </span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
        {blocks.map((b) => {
          const meta = BLOCK_META[b.color] || BLOCK_META.orange;
          return (
            <div key={b.id} className="flex items-center gap-2 text-xs">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: meta.hex }} />
              <span className="font-mono text-muted-foreground">{String(b.id).padStart(2, "0")}</span>
              <span className="text-foreground">{b.title}</span>
              <span className="text-muted-foreground">· {b.time}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Single block — clean horizontal layout (icon + content)
// No side info cards (palanca/rol/tono removed per design refresh).
// ============================================================
function BlockCard({ block, index }) {
  const meta = BLOCK_META[block.color] || BLOCK_META.orange;
  const Icon = meta.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, ease: "easeOut" }}
      className="relative grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-10 items-start"
    >
      {/* Icon column */}
      <div className="flex lg:flex-col items-center lg:items-start gap-4 lg:gap-3 lg:pt-2">
        <div
          className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl flex items-center justify-center shadow-xl ring-4 ring-background relative z-10 flex-shrink-0"
          style={{ backgroundColor: meta.hex }}
        >
          <Icon className="w-7 h-7 lg:w-9 lg:h-9 text-white" />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-mono text-muted-foreground">BLOQUE {String(block.id).padStart(2, "0")}</span>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider self-start"
            style={{ backgroundColor: meta.soft, color: meta.hex }}
          >
            <Clock className="w-3 h-3" />
            {block.time}
          </span>
        </div>
      </div>

      {/* Content column */}
      <div>
        <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-5">{block.title}</h3>

        {block.highlights && (
          <ul className="space-y-3">
            {block.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                <span className="mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.hex }} />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        {block.subBlocks && (
          <div className="space-y-5 mt-2">
            {block.subBlocks.map((sb, k) => (
              <div key={k} className="p-5 rounded-xl border border-border bg-card/40">
                <div className="font-semibold text-sm mb-3" style={{ color: meta.hex }}>{sb.title}</div>
                <ul className="space-y-2">
                  {sb.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: meta.hex }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================
// Objection accordion
// ============================================================
function Objection({ q, a, index, open, onClick }) {
  return (
    <div className="border border-border rounded-2xl overflow-hidden bg-card/40">
      <button
        type="button"
        onClick={onClick}
        data-testid={`objection-${index}`}
        className="w-full flex items-center justify-between gap-4 p-5 text-left group"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-muted-foreground">{String(index + 1).padStart(2, "0")}</span>
          <span className="font-semibold">{q}</span>
        </div>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? "rotate-180 text-dc-amber-light dark:text-dc-amber" : "text-muted-foreground"}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="overflow-hidden"
      >
        <div className="px-5 pb-5 pt-1 text-sm leading-relaxed text-muted-foreground border-t border-border/60 pl-12">
          {a}
        </div>
      </motion.div>
    </div>
  );
}

// ============================================================
// Page
// ============================================================
export default function ConferenciasPage() {
  const { t } = useTranslation();
  const [openObj, setOpenObj] = useState(0);

  const blocks = t("conferencias.blocks", { returnObjects: true });
  const objections = t("conferencias.objections", { returnObjects: true });
  const benefits = t("conferencias.benefits.items", { returnObjects: true });

  return (
    <main>
      <PageHero
        eyebrow={t("conferencias.hero.eyebrow")}
        title={t("conferencias.hero.title")}
        subtitle={t("conferencias.hero.subtitle")}
        image={HERO_IMG}
        imageAlt="Conferencia ejecutiva sobre IA"
      >
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
          <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-dc-amber-light dark:text-dc-amber" /> 2 horas</div>
          <div className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-dc-cyan-light dark:text-dc-cyan" /> Presencial o virtual</div>
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-dc-amber-light dark:text-dc-amber" /> En español, sin tecnicismos</div>
        </div>
      </PageHero>

      {/* ============ TIMELINE BAR ============ */}
      <section className="section-y border-b border-border">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="eyebrow mb-4">{t("conferencias.metaTitle")}</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Activa la IA. Domina tu mercado.
            </h2>
            <p className="text-muted-foreground text-lg">{t("conferencias.metaSubtitle")}</p>
          </motion.div>
          <TimelineBar blocks={blocks} />
        </div>
      </section>

      {/* ============ BLOCKS — alternating layout ============ */}
      <section className="section-y relative">
        {/* central spine on desktop */}
        <div className="hidden lg:block absolute top-32 bottom-32 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-border to-transparent" />
        <div className="container-page relative">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-16 max-w-3xl">
            Estructura de la Conferencia
          </motion.h2>
          <div className="space-y-20 lg:space-y-28">
            {blocks.map((b, i) => (
              <BlockCard key={b.id} block={b} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="section-y bg-secondary/30 border-y border-border">
        <div className="container-page grid lg:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <div className="eyebrow mb-4">Resultados</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">{t("conferencias.benefits.title")}</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Cada sesión termina con tu comité ejecutivo con la mente clara y una lista concreta de próximos pasos.
            </p>
          </motion.div>
          <motion.ul {...fadeUp} className="space-y-4">
            {benefits.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-5 p-6 rounded-2xl bg-card/60 border border-border hover:border-dc-amber-light dark:hover:border-dc-amber transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-dc-amber-light/15 dark:bg-dc-amber/20 text-dc-amber-light dark:text-dc-amber flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <span className="leading-relaxed text-base md:text-lg pt-2">{b}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ============ OBJECTIONS — Accordion ============ */}
      <section className="section-y">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-3xl mb-12">
            <div className="eyebrow mb-4">FAQ</div>
            <h2 className="text-3xl sm:text-4xl font-bold">{objections.title}</h2>
            {objections.subtitle && (
              <p className="mt-3 text-muted-foreground text-lg">{objections.subtitle}</p>
            )}
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-3 max-w-5xl">
            {objections.items.map((o, i) => (
              <Objection
                key={i}
                index={i}
                q={o.q}
                a={o.a}
                open={openObj === i}
                onClick={() => setOpenObj(openObj === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ FORM ============ */}
      <section className="section-y bg-secondary/30 border-t border-border">
        <div className="container-page max-w-3xl">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-3 text-center">
            Reserva tu conferencia
          </motion.h2>
          <p className="text-center text-muted-foreground mb-10">
            Cuéntanos cuándo y dónde. Te confirmamos disponibilidad en menos de 24 horas.
          </p>
          <div className="glass-card p-8 md:p-10">
            <LeadForm pageOrigin="conferencias" defaultService="conferencias" />
          </div>
        </div>
      </section>
    </main>
  );
}
